/**
 * The Altar Forge actions (crafting.md §3–§5): craft, temper, masterwork, drill, refine, gild,
 * catalyze, salvage, combine, gifting and the Weekly Ritual claim. Every action is pure (returns
 * a new Profile, never mutates its input) and every roll draws from
 * `profileRng(p, FORGE_SALT ^ p.forge.rollIndex)`, after which rollIndex += 1, so the same
 * profile + the same action always gives the same result and reopening a screen can't reroll.
 *
 * Nothing is ever destroyed or downgraded: a failed roll leaves the item exactly as it was
 * (apart from its pity counters) and refunds part of the cost.
 *
 * Draw order (part of the save contract, crafting.md §9.3):
 *   craft:  1 rarity r · 2 Eye legendary roll (+ pick if the slot has >1 candidate) ·
 *           3 trinket effect: chance, then pick · 4 secondaries (minus locks) · 5 qualities (2) ·
 *           6 sockets: first, then second
 *   temper: 1 success r · 2 Surge (on success) · 3 Awakening outcome · 4 Empowered stat pick
 *   masterwork / drill / gild: 1 success r
 *   refine: 1 per rerolled quality
 */
import type { ClassId, Rng } from '@keystone/sim';
import { addCodex, codexAwakenId, codexGildId, codexIdsForItem } from './codex';
import { CLASS_STAT_PROFILES } from './data';
import {
  AWAKEN_EVERY,
  AWAKEN_TABLE,
  COMBINE_GOLD,
  COMBINE_RATIO,
  CRAFT_SOCKET_FIRST,
  CRAFT_SOCKET_SECOND,
  ECHO_VENOM,
  FORGE_HISTORY_MAX,
  FORGE_SALT,
  GILD_MAX,
  QUALITY_MAX,
  QUALITY_MIN,
  QUALITY_STEPS,
  RESONANCE_MAX,
  RESONANCE_NEAR_MISS,
  RESONANCE_PER_FAIL,
  SOCKET_SLOT_BONUS,
  SURGE_CHANCE,
  TIER_SET_IDS,
  TRINKET_EFFECT_BY_RARITY,
  gradeForKey,
  gradeIndex,
} from './forge.constants';
import type { AwakeningKind } from './forge.constants';
import { emptyItemForge, itemForge, rarityUp, salvageGems, salvageYield } from './forge.items';
import {
  catalystCost,
  craftOdds,
  drillMaxSockets,
  drillOdds,
  gildOdds,
  isEpicNearMiss,
  isTemperNearMiss,
  masterworkOdds,
  eyeAttuneOdds, // +forge-sinks
  sigilReshapeOdds, // +forge-sinks
  rarityForRoll,
  refineCost,
  refundOf,
  temperOdds,
} from './forge.odds';
import type { ChanceBreakdown, CraftOdds, CraftSpec, LuckBreakdown, RarityBands, RarityOdds, RollBoosts, TemperOpts } from './forge.odds';
import { addBundles, credit, debit, emptySatchel, scaleCount, shortfall } from './forge.wallet';
import { addGem } from './gems';
import { MAX_SOCKETS, PRIORITY_ROLL_WEIGHTS, rescaleSecondaries, secondaryRating } from './items';
import { LEGENDARY_SOCKETS, legendariesFor } from './items.legendaries';
import { trinketsForRole } from './items.trinkets';
import { ritualCache, weeklyReady } from './payout';
import type { RitualChoice } from './payout';
import { equipItem, itemById, itemOwner, profileRng, profileStanding, roleOf } from './profile';
import type { OpError } from './profile';
import { RANK_TIER_ORDER } from './types';
import { isTierSlot } from './sets';
import type { ForgeHistoryEntry, Item, ItemId, Profile, Rarity, ResourceBundle, Satchel, ScaleGrade, SecondaryStat } from './types';
import { SCALE_GRADES, SECONDARY_STATS } from './types';

// ============================================================================
// Result types
// ============================================================================

export type ForgeFail = { ok: false; reason: OpError };

export interface CraftRoll {
  /** The rarity roll, r ∈ [0,100). */
  r: number;
  bands: RarityBands;
  /** Odds the roll used (after floors and pity). */
  odds: RarityOdds;
  rarity: Rarity;
  /** Landed within 5 below the Epic band (counted double toward Epic pity). */
  nearMiss: boolean;
  luck: LuckBreakdown;
  fortune: boolean;
  floor: Rarity | null;
  /** Eye crafts: the legendary roll (1..100, hit when r ≤ needed). */
  legendary: { r: number; needed: number; hit: boolean } | null;
  tierPiece: boolean;
  pityBefore: { sinceEpic: number; belowRare: number; eyeMisses: number };
  pityAfter: { sinceEpic: number; belowRare: number; eyeMisses: number };
  /** Codex entries this craft added. */
  codexNew: string[];
}

export type CraftResult = { ok: true; profile: Profile; item: Item; roll: CraftRoll } | ForgeFail;

export interface AwakeningResult {
  kind: AwakeningKind;
  /** The rolled kind (before fallbacks, e.g. Ascend on an Epic becomes Empowered). */
  rolled: AwakeningKind;
  /** Empowered: the secondary index. */
  statIndex?: number;
  /** Echo: what was refunded. */
  refund?: ResourceBundle;
}

export interface ForgeRoll {
  kind: 'temper' | 'masterwork' | 'drill' | 'gild';
  outcome: 'success' | 'surge' | 'fail';
  /** The roll, 1..100; success when r ≤ needed. */
  r: number;
  needed: number;
  odds: ChanceBreakdown;
  nearMiss: boolean;
  /** What a failure refunded (empty on success). */
  refund: ResourceBundle;
  /** Resources the attempt took (before the refund). */
  paid: ResourceBundle;
  resonanceBefore: number;
  resonanceAfter: number;
  failsAfter: number;
  ilvlBefore: number;
  ilvlAfter: number;
  awakening: AwakeningResult | null;
  codexNew: string[];
  /** +forge-sinks: this attempt was the free one. */
  free?: boolean;
}

export type RollResult = { ok: true; profile: Profile; item: Item; roll: ForgeRoll } | ForgeFail;
export type ForgeOpResult = { ok: true; profile: Profile } | ForgeFail;

const fail = (reason: OpError): ForgeFail => ({ ok: false, reason });

// ============================================================================
// Internals
// ============================================================================

/** The forge RNG for the next roll; advances rollIndex on the (cloned) profile. */
export function forgeRng(p: Profile): Rng {
  const rng = profileRng(p, (FORGE_SALT ^ p.forge.rollIndex) >>> 0);
  p.forge.rollIndex += 1;
  return rng;
}

function replaceItem(p: Profile, it: Item): void {
  const i = p.inventory.findIndex((x) => x.id === it.id);
  if (i >= 0) p.inventory[i] = it;
}

function allocItemId(p: Profile): ItemId {
  const id = `i${p.nextId}`;
  p.nextId += 1;
  return id;
}

function pushHistory(p: Profile, e: ForgeHistoryEntry): void {
  p.forge.history = [...p.forge.history, e].slice(-FORGE_HISTORY_MAX);
}

/** A character's satchel, created on demand. */
export function satchelOf(p: Profile, classId: ClassId): Satchel {
  const ch = p.characters[classId]!;
  ch.satchel ??= emptySatchel();
  return ch.satchel;
}

function payDebit(p: Profile, payer: 'wallet' | 'satchel', classId: ClassId | undefined, b: ResourceBundle): void {
  if (payer === 'wallet') return debit(p, b);
  const s = satchelOf(p, classId!);
  s.fangsteel -= b.fangsteel ?? 0;
  s.venom -= b.venom ?? 0;
  s.moltskin -= b.moltskin ?? 0;
  for (const g of SCALE_GRADES) s.scales[g] -= b.scales?.[g] ?? 0;
}

function payCredit(p: Profile, payer: 'wallet' | 'satchel', classId: ClassId | undefined, b: ResourceBundle): void {
  if (payer === 'wallet') {
    credit(p, b);
    return;
  }
  const s = satchelOf(p, classId!);
  s.fangsteel += b.fangsteel ?? 0;
  s.venom += b.venom ?? 0;
  s.moltskin += b.moltskin ?? 0;
  for (const g of SCALE_GRADES) s.scales[g] += b.scales?.[g] ?? 0;
}

const blockReason = (b: string): OpError => b as OpError;

/** One quality draw: 0.90..1.10 in 0.01 steps. */
function rollQuality(rng: Rng): number {
  const k = Math.min(QUALITY_STEPS, Math.floor(rng.next() * (QUALITY_STEPS + 1)));
  return Math.round((QUALITY_MIN + ((QUALITY_MAX - QUALITY_MIN) * k) / QUALITY_STEPS) * 100) / 100;
}

/** One weighted secondary draw from `pool` (class priority weights). */
function pickStat(rng: Rng, pool: readonly SecondaryStat[], priority: readonly SecondaryStat[]): SecondaryStat {
  const w = (s: SecondaryStat): number => {
    const i = priority.indexOf(s);
    return i < 0 ? 1 : (PRIORITY_ROLL_WEIGHTS[i] ?? 1);
  };
  const total = pool.reduce((t, s) => t + w(s), 0);
  let r = rng.next() * total;
  for (const s of pool) {
    r -= w(s);
    if (r < 0) return s;
  }
  return pool[pool.length - 1]!;
}

const atLeastTier = (p: Profile, min: (typeof RANK_TIER_ORDER)[number]): boolean => RANK_TIER_ORDER.indexOf(profileStanding(p).tier) >= RANK_TIER_ORDER.indexOf(min);

// ============================================================================
// Craft
// ============================================================================

/**
 * Craft an item at the Altar Forge. Pays the recipe (or a Vault Forge Token), rolls rarity with
 * the odds `craftOdds` shows, applies pity, and puts the item in the bag (or equips it with
 * `spec.equip`). A satchel craft (`payer: 'satchel'`) pays from that companion's purse with no
 * gold, base odds and the satchel's own pity, and does not touch the player's Codex or counters.
 */
export function craft(profile: Profile, spec: CraftSpec): CraftResult {
  const odds: CraftOdds = craftOdds(profile, spec);
  if (odds.blocked) return fail(blockReason(odds.blocked));
  const bot = spec.payer === 'satchel';
  if (bot) {
    const s = profile.characters[spec.classId]?.satchel;
    const short = s ? satchelShort(s, odds.cost) : odds.cost;
    if (!s || Object.keys(short).length) return fail('cantAfford');
  }
  const p = structuredClone(profile);
  const rng = forgeRng(p);
  if (bot) payDebit(p, 'satchel', spec.classId, odds.cost);
  else debit(p, odds.cost);
  if (spec.token !== undefined && !bot) p.wallet.vaultTokens.splice(spec.token, 1);

  // 1. rarity
  const r = rng.next() * 100;
  let rarity: Rarity = rarityForRoll(odds.bands, r);
  const nearMiss = isEpicNearMiss(odds.bands, r);
  // 2. Eye
  let legendaryId: string | undefined;
  let legendary: CraftRoll['legendary'] = null;
  if (spec.eye) {
    const lr = 1 + Math.floor(rng.next() * 100);
    const hit = lr <= (odds.legendary ?? 0);
    legendary = { r: lr, needed: odds.legendary ?? 0, hit };
    if (hit) {
      const all = legendariesFor(spec.classId).filter((l) => l.slot === spec.slot);
      const owned = new Set(p.inventory.map((i) => i.legendaryId).filter(Boolean));
      const fresh = all.filter((l) => !owned.has(l.id));
      const pool = fresh.length ? fresh : all;
      legendaryId = (pool.length > 1 ? rng.pick(pool) : pool[0])!.id;
      rarity = 'legendary';
    }
  }
  // 3. trinket effect
  let trinketEffectId: string | undefined;
  const molt = Math.floor(spec.moltskin ?? 0);
  if (spec.slot === 'trinket' && !legendaryId) {
    const pool = trinketsForRole(roleOf(spec.classId));
    if (molt >= 2 && spec.trinketEffectId && pool.some((t) => t.id === spec.trinketEffectId)) trinketEffectId = spec.trinketEffectId;
    else if (rng.next() < TRINKET_EFFECT_BY_RARITY[rarity]) trinketEffectId = rng.pick(pool)?.id;
  }
  // 4. secondaries
  const priority = CLASS_STAT_PROFILES[spec.classId]?.priority ?? SECONDARY_STATS;
  const locks = spec.slot === 'trinket' ? Math.min(1, molt) : molt;
  const stats: SecondaryStat[] = [...(spec.stats ?? []).slice(0, locks)];
  while (stats.length < 2) stats.push(pickStat(rng, SECONDARY_STATS.filter((s) => !stats.includes(s)), priority));
  // 5. qualities
  const qualities = [rollQuality(rng), rollQuality(rng)];
  // 6. sockets
  let sockets = 0;
  if (legendaryId) sockets = LEGENDARY_SOCKETS;
  else {
    const first = Math.min(1, CRAFT_SOCKET_FIRST[rarity] + (CRAFT_SOCKET_FIRST[rarity] > 0 ? (SOCKET_SLOT_BONUS[spec.slot] ?? 0) : 0));
    if (rng.next() < first) {
      sockets = 1;
      if (rng.next() < CRAFT_SOCKET_SECOND[rarity]) sockets = 2;
    }
  }
  sockets = Math.min(MAX_SOCKETS, sockets, Math.max(drillMaxSockets({ rarity }), legendaryId ? LEGENDARY_SOCKETS : 0));

  const item: Item = {
    id: allocItemId(p),
    slot: spec.slot,
    ilvl: odds.ilvl,
    rarity,
    secondaries: stats.map((stat, i) => ({ stat, rating: 0, quality: qualities[i]! })),
    sockets,
    gems: Array.from({ length: sockets }, () => null),
    source: 'craft',
    forge: emptyItemForge(),
  };
  item.secondaries = rescaleSecondaries(item, item.ilvl);
  if (legendaryId) item.legendaryId = legendaryId;
  if (trinketEffectId) item.trinketEffectId = trinketEffectId;
  if (spec.sigil && isTierSlot(spec.slot)) {
    const setId = TIER_SET_IDS[spec.classId];
    if (setId) item.setId = setId;
  }
  p.inventory.push(item);

  // pity + counters
  const before = bot
    ? { sinceEpic: p.characters[spec.classId]!.satchel!.sinceEpic, belowRare: p.characters[spec.classId]!.satchel!.belowRare, eyeMisses: 0 }
    : { sinceEpic: p.forge.sinceEpic, belowRare: p.forge.belowRare, eyeMisses: p.forge.eyeMisses };
  const epicPlus = rarity === 'epic' || rarity === 'legendary';
  const after = {
    sinceEpic: epicPlus ? 0 : before.sinceEpic + (nearMiss ? 2 : 1),
    belowRare: rarity === 'common' || rarity === 'uncommon' ? before.belowRare + 1 : 0,
    eyeMisses: spec.eye ? (legendaryId ? 0 : before.eyeMisses + 1) : before.eyeMisses,
  };
  let codexNew: string[] = [];
  if (bot) {
    const s = satchelOf(p, spec.classId);
    s.sinceEpic = after.sinceEpic;
    s.belowRare = after.belowRare;
  } else {
    p.forge.sinceEpic = after.sinceEpic;
    p.forge.belowRare = after.belowRare;
    p.forge.eyeMisses = after.eyeMisses;
    p.forge.craftCount += 1;
    codexNew = addCodex(p.forge, codexIdsForItem(item, true));
    pushHistory(p, { kind: 'craft', result: rarity, nearMiss });
  }

  let out = p;
  if (spec.equip) {
    const e = equipItem(p, spec.classId, item.id);
    if (e.ok) out = e.profile;
  }
  return {
    ok: true,
    profile: out,
    item,
    roll: {
      r,
      bands: odds.bands,
      odds: odds.rollOdds,
      rarity,
      nearMiss,
      luck: odds.luck,
      fortune: odds.fortune,
      floor: odds.floor,
      legendary,
      tierPiece: !!item.setId,
      pityBefore: before,
      pityAfter: after,
      codexNew,
    },
  };
}

function satchelShort(s: Satchel, cost: ResourceBundle): ResourceBundle {
  const out: ResourceBundle = {};
  if ((cost.fangsteel ?? 0) > s.fangsteel) out.fangsteel = (cost.fangsteel ?? 0) - s.fangsteel;
  if ((cost.venom ?? 0) > s.venom) out.venom = (cost.venom ?? 0) - s.venom;
  if ((cost.moltskin ?? 0) > s.moltskin) out.moltskin = (cost.moltskin ?? 0) - s.moltskin;
  for (const g of SCALE_GRADES) if ((cost.scales?.[g] ?? 0) > s.scales[g]) out.scales = { ...out.scales, [g]: (cost.scales?.[g] ?? 0) - s.scales[g] };
  return out;
}

// ============================================================================
// Temper
// ============================================================================

function resolvePayer(p: Profile, item: Item, opts: TemperOpts): { payer: 'wallet' | 'satchel'; classId: ClassId | undefined } {
  const payer = opts.payer ?? 'wallet';
  return { payer, classId: payer === 'satchel' ? (opts.classId ?? itemOwner(p, item.id) ?? undefined) : opts.classId };
}

/**
 * One temper attempt: +5 ilvl (a Surge gives +10) on success. A failure keeps the item intact,
 * refunds half the Fangsteel and Scales, adds +10 pp to the next try and fills Resonance
 * (+25, +35 on a near miss); at full Resonance the next attempt is guaranteed.
 */
export function temper(profile: Profile, itemId: ItemId, opts: TemperOpts = {}): RollResult {
  const item0 = itemById(profile, itemId);
  if (!item0) return fail('unknownItem');
  const { payer, classId } = resolvePayer(profile, item0, opts);
  if (payer === 'satchel' && !classId) return fail('unknownClass');
  const o = temperOdds(profile, item0, { ...opts, payer, ...(classId ? { classId } : {}) });
  if (o.blocked) return fail(blockReason(o.blocked));
  const p = structuredClone(profile);
  const rng = forgeRng(p);
  payDebit(p, payer, classId, o.cost);
  const item = structuredClone(item0);
  const f = itemForge(item);
  const resonanceBefore = f.resonance;
  const r = 1 + Math.floor(rng.next() * 100);
  const needed = o.odds.chance;
  const paidScales = scaleCount(o.cost);
  let outcome: ForgeRoll['outcome'];
  let refund: ResourceBundle = {};
  let nearMiss = false;
  let awakening: AwakeningResult | null = null;
  const codexNew: string[] = [];
  if (r <= needed) {
    const surge = rng.next() < SURGE_CHANCE && o.surgeTarget !== null;
    outcome = surge ? 'surge' : 'success';
    item.ilvl = surge ? o.surgeTarget! : o.target;
    const stepsBefore = f.steps;
    f.steps += surge ? 2 : 1;
    f.fails = 0;
    f.resonance = 0;
    f.invested += paidScales;
    item.forge = f;
    item.secondaries = rescaleSecondaries(item, item.ilvl);
    if (Math.floor(f.steps / AWAKEN_EVERY) > Math.floor(stepsBefore / AWAKEN_EVERY)) {
      awakening = awaken(p, item, rng, o.cost, payer, classId);
      if (payer === 'wallet') codexNew.push(...addCodex(p.forge, [codexAwakenId(awakening.kind)]));
    }
  } else {
    outcome = 'fail';
    nearMiss = isTemperNearMiss(r, needed);
    refund = refundOf(o.cost);
    payCredit(p, payer, classId, refund);
    f.fails += 1;
    f.resonance = Math.min(RESONANCE_MAX, f.resonance + (nearMiss ? RESONANCE_NEAR_MISS : RESONANCE_PER_FAIL));
    f.invested += paidScales - scaleCount(refund);
    item.forge = f;
  }
  replaceItem(p, item);
  if (o.free) p.forge.freeTempers = Math.max(0, (p.forge.freeTempers ?? 0) - 1); // +forge-sinks
  if (payer === 'wallet') pushHistory(p, { kind: 'temper', result: outcome, nearMiss });
  return {
    ok: true,
    profile: p,
    item,
    roll: {
      kind: 'temper',
      outcome,
      r,
      needed,
      odds: o.odds,
      nearMiss,
      refund,
      paid: o.cost,
      resonanceBefore,
      resonanceAfter: f.resonance,
      failsAfter: f.fails,
      ilvlBefore: item0.ilvl,
      ilvlAfter: item.ilvl,
      awakening,
      codexNew,
      ...(o.free ? { free: true } : {}),
    },
  };
}

/**
 * Awakening on every AWAKEN_EVERY-th step (mutates `item` and the payer). Draws: outcome, then
 * the Empowered stat pick when Empowered applies. Inapplicable outcomes fall back in the order
 * Empowered → Socket → Echo (Echo always applies), so a jackpot is never wasted.
 */
function awaken(p: Profile, item: Item, rng: Rng, stepCost: ResourceBundle, payer: 'wallet' | 'satchel', classId: ClassId | undefined): AwakeningResult {
  let x = rng.next();
  let rolled: AwakeningKind = AWAKEN_TABLE[AWAKEN_TABLE.length - 1]!.kind;
  for (const row of AWAKEN_TABLE) {
    if (x < row.chance) {
      rolled = row.kind;
      break;
    }
    x -= row.chance;
  }
  const f = item.forge!;
  const empowered = f.empowered ?? [];
  const canEmpower = item.secondaries.some((_, i) => !empowered.includes(i));
  const canSocket = item.sockets < Math.min(MAX_SOCKETS, drillMaxSockets(item));
  const canAscend = item.rarity !== 'epic' && item.rarity !== 'legendary';
  const order: AwakeningKind[] = [rolled, 'empowered', 'socket', 'echo'];
  const kind = order.find((k) => (k === 'empowered' ? canEmpower : k === 'socket' ? canSocket : k === 'ascend' ? canAscend : true))!;
  const res: AwakeningResult = { kind, rolled };
  if (kind === 'empowered') {
    const open = item.secondaries.map((_, i) => i).filter((i) => !empowered.includes(i));
    const idx = rng.pick(open)!;
    f.empowered = [...empowered, idx].sort();
    item.secondaries = item.secondaries.map((s, i) => (i === idx ? { ...s, rating: secondaryRating(item, i, item.ilvl) } : s));
    res.statIndex = idx;
  } else if (kind === 'socket') {
    item.sockets += 1;
    item.gems = [...item.gems, null];
  } else if (kind === 'ascend') {
    item.rarity = rarityUp(item.rarity);
    item.secondaries = rescaleSecondaries(item, item.ilvl);
  } else {
    const refund: ResourceBundle = { venom: ECHO_VENOM };
    if (stepCost.scales) refund.scales = { ...stepCost.scales };
    payCredit(p, payer, classId, refund);
    res.refund = refund;
  }
  return res;
}

/**
 * Temper repeatedly ("Temper ×N"): stops after the first success/Surge, on any Awakening, when
 * the next attempt is blocked (resources), or after `maxAttempts`. Returns every roll.
 */
export function temperUntil(profile: Profile, itemId: ItemId, opts: TemperOpts = {}, maxAttempts = 5): { profile: Profile; rolls: ForgeRoll[]; stopped: 'success' | 'blocked' | 'max'; blocked: OpError | null } {
  let p = profile;
  const rolls: ForgeRoll[] = [];
  for (let i = 0; i < maxAttempts; i++) {
    const r = temper(p, itemId, opts);
    if (!r.ok) return { profile: p, rolls, stopped: 'blocked', blocked: r.reason };
    p = r.profile;
    rolls.push(r.roll);
    if (r.roll.outcome !== 'fail') return { profile: p, rolls, stopped: 'success', blocked: null };
  }
  return { profile: p, rolls, stopped: 'max', blocked: null };
}

// ============================================================================
// Masterwork, drill, gild (same pity rules)
// ============================================================================

function simpleRoll(
  profile: Profile,
  itemId: ItemId,
  kind: 'masterwork' | 'drill' | 'gild',
  pre: { odds: ChanceBreakdown; cost: ResourceBundle; refundOnFail: ResourceBundle; blocked: string | null },
  payer: 'wallet' | 'satchel',
  classId: ClassId | undefined,
  onSuccess: (item: Item) => void,
): RollResult {
  if (pre.blocked) return fail(blockReason(pre.blocked));
  const item0 = itemById(profile, itemId)!;
  const p = structuredClone(profile);
  const rng = forgeRng(p);
  payDebit(p, payer, classId, pre.cost);
  const item = structuredClone(item0);
  const f = itemForge(item);
  const resonanceBefore = f.resonance;
  const r = 1 + Math.floor(rng.next() * 100);
  const needed = pre.odds.chance;
  let refund: ResourceBundle = {};
  let nearMiss = false;
  const codexNew: string[] = [];
  const outcome: ForgeRoll['outcome'] = r <= needed ? 'success' : 'fail';
  if (outcome === 'success') {
    f.fails = 0;
    f.resonance = 0;
    if (kind === 'masterwork') f.invested += scaleCount(pre.cost);
    item.forge = f;
    onSuccess(item);
    if (kind === 'gild' && (item.forge?.gilding ?? 0) >= GILD_MAX && payer === 'wallet') codexNew.push(...addCodex(p.forge, [codexGildId(item.slot)]));
  } else {
    nearMiss = isTemperNearMiss(r, needed);
    refund = pre.refundOnFail;
    payCredit(p, payer, classId, refund);
    f.fails += 1;
    f.resonance = Math.min(RESONANCE_MAX, f.resonance + (nearMiss ? RESONANCE_NEAR_MISS : RESONANCE_PER_FAIL));
    if (kind === 'masterwork') f.invested += scaleCount(pre.cost) - scaleCount(refund);
    item.forge = f;
  }
  replaceItem(p, item);
  if (payer === 'wallet') pushHistory(p, { kind, result: outcome, nearMiss });
  return {
    ok: true,
    profile: p,
    item,
    roll: {
      kind,
      outcome,
      r,
      needed,
      odds: pre.odds,
      nearMiss,
      refund,
      paid: pre.cost,
      resonanceBefore,
      resonanceAfter: f.resonance,
      failsAfter: f.fails,
      ilvlBefore: item0.ilvl,
      ilvlAfter: item.ilvl,
      awakening: null,
      codexNew,
    },
  };
}

/**
 * One masterwork attempt (+1 effective ilvl) on an item at the tier cap, up to its rarity's
 * ceiling (MASTERWORK_CEILING, at most +3). 70 / 55 / 40 % with failstack + Resonance.
 */
export function masterworkItem(profile: Profile, itemId: ItemId, opts: TemperOpts = {}): RollResult {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  const { payer, classId } = resolvePayer(profile, item, opts);
  const o = masterworkOdds(profile, item, { ...opts, payer, ...(classId ? { classId } : {}) });
  const res = simpleRoll(profile, itemId, 'masterwork', o, payer, classId, (it) => {
    it.masterwork = (it.masterwork ?? 0) + 1;
  });
  // +forge-sinks: the free attempt is spent (res.profile is already a fresh clone).
  if (res.ok && o.free) {
    res.profile.forge.freeTempers = Math.max(0, (res.profile.forge.freeTempers ?? 0) - 1);
    res.roll.free = true;
  }
  return res;
}

/** Drill a socket (Venom + gold, 50% base, up to the rarity's socket cap). */
export function drillSocket(profile: Profile, itemId: ItemId, boosts: RollBoosts = {}): RollResult {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  return simpleRoll(profile, itemId, 'drill', drillOdds(profile, item, boosts), 'wallet', undefined, (it) => {
    it.sockets += 1;
    it.gems = [...it.gems, null];
  });
}

/** Cosmetic Gilding rank (no stats). */
export function gild(profile: Profile, itemId: ItemId, boosts: RollBoosts = {}): RollResult {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  return simpleRoll(profile, itemId, 'gild', gildOdds(profile, item, boosts), 'wallet', undefined, (it) => {
    it.forge = { ...it.forge!, gilding: (it.forge?.gilding ?? 0) + 1 };
  });
}

// ============================================================================
// Refine (keep-or-replace stat quality)
// ============================================================================

/**
 * Reroll an item's secondary qualities (1 Moltskin: both; 2 Moltskin + keepIndex: the other
 * one). The item is NOT changed: the new qualities wait in forge.pendingRefine until
 * keepRefine / discardRefine.
 */
export function refine(profile: Profile, itemId: ItemId, opts: { keepIndex?: number } = {}): ForgeOpResult & { qualities?: number[] } {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  if (profile.forge.pendingRefine) return fail('pendingRefine');
  const keep = opts.keepIndex;
  if (keep !== undefined && (keep < 0 || keep >= item.secondaries.length)) return fail('badIndex');
  const cost = refineCost(item, keep !== undefined ? 2 : 1);
  const short = shortfall(profile, cost);
  if (Object.keys(short).length) return fail(Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford');
  const p = structuredClone(profile);
  const rng = forgeRng(p);
  debit(p, cost);
  const qualities = item.secondaries.map((s, i) => (i === keep ? (s.quality ?? 1) : rollQuality(rng)));
  p.forge.pendingRefine = { itemId, qualities };
  return { ok: true, profile: p, qualities };
}

/** Apply the pending refine to its item. */
export function keepRefine(profile: Profile): ForgeOpResult {
  const pr = profile.forge.pendingRefine;
  if (!pr) return fail('noRefine');
  const p = structuredClone(profile);
  p.forge.pendingRefine = null;
  const item = itemById(p, pr.itemId);
  if (!item) return { ok: true, profile: p };
  const next: Item = { ...item, secondaries: item.secondaries.map((s, i) => ({ ...s, stat: pr.stats?.[i] ?? s.stat, quality: pr.qualities[i] ?? s.quality ?? 1 })) };
  // +forge-sinks: a Sigil Reshape that changed a stat clears the old reforge (it pointed at the old stat).
  if (pr.stats && next.reforged && pr.stats[next.reforged.index] !== item.secondaries[next.reforged.index]?.stat) delete next.reforged;
  next.secondaries = rescaleSecondaries(next, next.ilvl);
  replaceItem(p, next);
  return { ok: true, profile: p };
}

/** Throw the pending refine away (the item keeps its old qualities). */
export function discardRefine(profile: Profile): ForgeOpResult {
  if (!profile.forge.pendingRefine) return fail('noRefine');
  const p = structuredClone(profile);
  p.forge.pendingRefine = null;
  return { ok: true, profile: p };
}

// ============================================================================
// +forge-sinks: Sigil Reshape and Eye Attunement (crafting.md §4.6)
// ============================================================================

/**
 * Sigil Reshape: 1 Altar Sigil + gold rerolls a tier piece's two secondary STATS (class-priority
 * weighted, never duplicates) and their qualities. With `keepIndex` that secondary (stat and
 * quality) stays. Keep-or-replace like Refine: the result waits in forge.pendingRefine (with
 * `stats`) until keepRefine / discardRefine, so a Sigil never makes an item worse.
 * Draws: the non-kept stat picks, then one quality per rerolled secondary.
 */
export function sigilReshape(profile: Profile, itemId: ItemId, opts: { keepIndex?: number } = {}): ForgeOpResult & { stats?: SecondaryStat[]; qualities?: number[] } {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  const o = sigilReshapeOdds(profile, item, opts);
  if (o.blocked) return fail(blockReason(o.blocked));
  const p = structuredClone(profile);
  const rng = forgeRng(p);
  debit(p, o.cost);
  const cid = itemOwner(profile, itemId) ?? profile.mainClassId;
  const priority = CLASS_STAT_PROFILES[cid]?.priority ?? SECONDARY_STATS;
  const keep = opts.keepIndex;
  const stats: (SecondaryStat | null)[] = item.secondaries.map((s, i) => (i === keep ? s.stat : null));
  for (let i = 0; i < stats.length; i++) {
    if (stats[i]) continue;
    const taken = stats.filter((x): x is SecondaryStat => !!x);
    stats[i] = pickStat(rng, SECONDARY_STATS.filter((s) => !taken.includes(s)), priority);
  }
  const qualities = item.secondaries.map((s, i) => (i === keep ? (s.quality ?? 1) : rollQuality(rng)));
  p.forge.pendingRefine = { itemId, qualities, stats: stats as SecondaryStat[] };
  return { ok: true, profile: p, stats: stats as SecondaryStat[], qualities };
}

/**
 * Eye Attunement: EYE_ATTUNE_FRAGMENTS Eye fragments + gold advance Eye pity one step, so the
 * next Serpent's Eye rolls Legendary at 55 / 80 / 100% instead of 30. A sink for late or odd
 * fragments (fewer than an Eye, or piling up before Champion). No RNG.
 */
export function attuneEye(profile: Profile): ForgeOpResult & { chance?: number } {
  const o = eyeAttuneOdds(profile);
  if (o.blocked) return fail(blockReason(o.blocked));
  const p = structuredClone(profile);
  debit(p, o.cost);
  p.forge.eyeMisses += 1;
  return { ok: true, profile: p, chance: o.chanceAfter };
}

// ============================================================================
// Catalyst, salvage, combine, gifts, weekly
// ============================================================================

/**
 * Catalyst: 1 Sigil + 10 Fangsteel turn a non-legendary item in a tier slot into `classId`'s
 * tier piece, keeping ilvl, rarity, stats, gems and temper state. classId defaults to the
 * wearer (or the main character for a bag item).
 */
export function catalyze(profile: Profile, itemId: ItemId, classId?: ClassId): ForgeOpResult & { item?: Item } {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  const cid = classId ?? itemOwner(profile, itemId) ?? profile.mainClassId;
  if (!profile.characters[cid]) return fail('unknownClass');
  if (!isTierSlot(item.slot)) return fail('notTierSlot');
  if (item.legendaryId) return fail('isLegendary');
  const setId = TIER_SET_IDS[cid];
  if (!setId) return fail('wrongClass');
  if (item.setId === setId) return fail('alreadyTier');
  const owner = itemOwner(profile, itemId);
  if (owner && owner !== cid) return fail('equippedElsewhere');
  const cost = catalystCost();
  if (Object.keys(shortfall(profile, cost)).length) return fail('cantAfford');
  const p = structuredClone(profile);
  debit(p, cost);
  // Converting another set's piece gives that piece's Sigil back (a Sigil is never lost).
  if (item.setId) credit(p, { sigils: 1 });
  const next: Item = { ...structuredClone(item), setId };
  replaceItem(p, next);
  addCodex(p.forge, codexIdsForItem(next, false));
  return { ok: true, profile: p, item: next };
}

/**
 * Salvage an unequipped item (replaces scrap): Fangsteel by ilvl + rarity, half its tempering
 * Scales, 1 Moltskin if Epic+, its gems, and its Sigil if it is a tier piece.
 */
export function salvage(profile: Profile, itemId: ItemId): ForgeOpResult & { gained?: ResourceBundle } {
  const item = itemById(profile, itemId);
  if (!item) return fail('unknownItem');
  if (itemOwner(profile, itemId)) return fail('equipped');
  const p = structuredClone(profile);
  const gained = salvageYield(item);
  credit(p, gained);
  for (const g of salvageGems(item)) p.gems = addGem(p.gems, g);
  p.inventory = p.inventory.filter((i) => i.id !== itemId);
  if (p.forge.pendingRefine?.itemId === itemId) p.forge.pendingRefine = null;
  return { ok: true, profile: p, gained };
}

/** Old name of salvage (the web's inventory/loot screens). */
export const scrapItem = (profile: Profile, itemId: ItemId): ForgeOpResult => salvage(profile, itemId);

/** Gold fee of combining into `count` Scales of the grade above `from`. */
export function combineFee(from: ScaleGrade, count: number): number {
  return COMBINE_GOLD * (gradeIndex(from) + 1) * count;
}

/** Combine COMBINE_RATIO × count Scales of `from` into `count` of the next grade (never automatic for the player). */
export function combineScales(profile: Profile, from: ScaleGrade, count: number, payer: 'wallet' | 'satchel' = 'wallet', classId?: ClassId): ForgeOpResult {
  const gi = gradeIndex(from);
  if (gi < 0 || gi >= SCALE_GRADES.length - 1) return fail('badTarget');
  if (!Number.isInteger(count) || count <= 0) return fail('badTarget');
  const to = SCALE_GRADES[gi + 1]!;
  const cost: ResourceBundle = { scales: { [from]: COMBINE_RATIO * count } };
  if (payer === 'wallet') cost.gold = combineFee(from, count);
  if (payer === 'satchel') {
    const s = classId ? profile.characters[classId]?.satchel : undefined;
    if (!s || s.scales[from] < COMBINE_RATIO * count) return fail('cantAfford');
  } else {
    const short = shortfall(profile, cost);
    if (Object.keys(short).length) return fail(Object.keys(short).length === 1 && short.gold ? 'notEnoughGold' : 'cantAfford');
  }
  const p = structuredClone(profile);
  payDebit(p, payer, classId, cost);
  payCredit(p, payer, classId, { scales: { [to]: count } });
  return { ok: true, profile: p };
}

/** "Combine all spare <grade> (keeps `keep`)": as many combines as the spare Scales (and gold) allow. */
export function combineSpare(profile: Profile, from: ScaleGrade, keep = 6): ForgeOpResult & { made?: number } {
  const spare = Math.max(0, profile.wallet.scales[from] - keep);
  let n = Math.floor(spare / COMBINE_RATIO);
  const per = combineFee(from, 1);
  if (per > 0) n = Math.min(n, Math.floor(profile.currencies.gold / per));
  if (n <= 0) return fail('notEnough');
  const r = combineScales(profile, from, n);
  return r.ok ? { ...r, made: n } : r;
}

/** Move Fangsteel / Scales / Venom / Moltskin from the wallet to a companion's satchel (one way). */
export function giftToSatchel(profile: Profile, classId: ClassId, gift: ResourceBundle): ForgeOpResult {
  if (!profile.characters[classId]) return fail('unknownClass');
  const b: ResourceBundle = addBundles({ fangsteel: gift.fangsteel, venom: gift.venom, moltskin: gift.moltskin, scales: gift.scales });
  if (Object.keys(shortfall(profile, b)).length) return fail('cantAfford');
  const p = structuredClone(profile);
  debit(p, b);
  payCredit(p, 'satchel', classId, b);
  return { ok: true, profile: p };
}

/** Best Scale grade the player has earned (from the best key on record). */
export function bestGrade(p: Profile): ScaleGrade {
  let best = 2;
  for (const rec of Object.values(p.rating)) best = Math.max(best, rec.best?.level ?? 0, rec.bestAttempt?.level ?? 0);
  return gradeForKey(best);
}

/** Claim the Weekly Ritual cache (4 completed runs in the caller's ISO week). 'eye' needs Champion+. */
export function claimWeeklyRitual(profile: Profile, choice: RitualChoice, localDate: string): ForgeOpResult & { gained?: ResourceBundle } {
  if (!weeklyReady(profile.forge.weekly, localDate)) return fail('notReady');
  if (choice === 'eye' && !atLeastTier(profile, 'champion')) return fail('locked');
  const p = structuredClone(profile);
  const gained = ritualCache(choice, bestGrade(p));
  credit(p, gained);
  p.forge.weekly = { ...p.forge.weekly, claimed: true };
  return { ok: true, profile: p, gained };
}
